import React, { useState } from 'react';
import { Box, Typography, Stack, Paper } from '@mui/material';
import { ByzMuiDateTimePicker } from 'src/components/core-components/mui/inputs/ByzMuiDateTimePicker';
import { useTranslate } from 'src/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Iconify from 'src/components/iconify';
import ByzMuiButton from 'src/components/core-components/mui/buttons/ByzMuiButton';
import ByzMuiSaveButton from 'src/components/core-components/mui/buttons/ByzMuiSaveButton';

// Set dayjs locale to Turkish
dayjs.locale('tr');

interface ChangeApplyTimeProps {
    initialTime?: string | Date | null;
    onClose: () => void;
    onSave: (newTime: Date) => void;
}

const ChangeApplyTime: React.FC<ChangeApplyTimeProps> = ({ initialTime, onClose, onSave }) => {
    const { t } = useTranslate();
    const [selectedTime, setSelectedTime] = useState<any>(initialTime ? dayjs(initialTime) : dayjs());

    const handleSave = () => {
        if (selectedTime?.isValid?.()) {
            onSave(selectedTime.toDate());
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
            <Box sx={{ p: 3, minWidth: '400px' }}>
                <Stack spacing={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Iconify icon="solar:clock-circle-bold-duotone" width={28} sx={{ color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('nurse.order-medicine.dialogs.change-apply-time.title')}
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        {t('nurse.order-medicine.dialogs.change-apply-time.description')}
                    </Typography>

                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            bgcolor: 'background.neutral',
                            borderStyle: 'dashed',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                        }}
                    >
                        <ByzMuiDateTimePicker
                            label={t('nurse.order-medicine.dialogs.change-apply-time.label')}
                            value={selectedTime}
                            onChange={(newValue) => setSelectedTime(newValue)}
                            width="100%"
                            format="DD.MM.YYYY HH:mm"
                            ampm={false}
                        />
                    </Paper>

                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <ByzMuiButton
                            onClick={onClose}
                            differentColor="light_blue"
                            sx={{
                                borderRadius: '8px',
                                px: 3,
                                border: '1px solid #767a85',
                                color: '#767a85',
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: '#f4f6f8' }
                            }}
                        >
                            {t('nurse.order-medicine.detail.cancel')}
                        </ByzMuiButton>
                        <ByzMuiSaveButton
                            onClick={handleSave}
                            sx={{
                                borderRadius: '8px',
                                px: 4,
                            }}
                        >
                            {t('nurse.order-medicine.detail.done')}
                        </ByzMuiSaveButton>
                    </Stack>
                </Stack>
            </Box>
        </LocalizationProvider>
    );
};

export default ChangeApplyTime;
